import { clsx } from 'clsx'
import { useState, type ComponentPropsWithoutRef, type ReactElement } from 'react'

import { generateRandomKey } from '@lib/utils/strings'

import { IconSprites } from '@constants/enums/sprites'

import { Button } from '@components/ui-core/button/button'
import { Icon } from '@components/ui-core/icon/icon'
import { Menu } from '@components/ui-core/menu/menu'
import { Text } from '@components/ui-core/text/text'
import { type NavLinkItem } from '@components/ui-core/types'

import { Column } from '../column/column'

import { NavLink } from './nav-link'

import styles from './nav-links.module.scss'

type NavLinksCategory = 'primary' | 'secondary'

function NavLinkItem({
  link,
  category,
}: {
  link: NavLinkItem
  category: NavLinksCategory
}): ReactElement {
  const { href, label, links, openIn, icon } = link
  const [isOpen, setIsOpen] = useState(false)
  const isPrimary = category === 'primary'

  if (href) {
    return <NavLink href={href} label={label} openIn={openIn} icon={icon} />
  }

  if (!links) {
    return <div>{label}</div>
  }

  // Internal component for the trigger button
  function Trigger({ isMenuOpen }: { isMenuOpen: boolean }): ReactElement {
    return (
      <Button
        category="unstyled"
        className={styles.navLink}
        onClick={isPrimary ? undefined : () => setIsOpen(!isOpen)}
      >
        <Text as="span">{label}</Text>
        <Icon
          variant={IconSprites.ArrowDown}
          className={clsx(styles.icon, isMenuOpen && styles.iconRotated)}
        />
      </Button>
    )
  }

  // Helper function for rendering list - this is fine as a function
  // because it's pure rendering logic without component semantics
  const renderLinks = () => (
    <Column as="ul" gap="4x" className={clsx(isPrimary && styles.navLinkItemColumn)}>
      {links.map(link => (
        <li className={styles.navMenuListItem} key={generateRandomKey()}>
          <NavLink
            href={link.href}
            label={link.label}
            openIn={link.openIn}
            icon={link.icon}
            className={styles.navLinkItem}
          />
        </li>
      ))}
    </Column>
  )

  if (isPrimary) {
    return (
      <Menu
        trigger={<Trigger isMenuOpen={isOpen} />}
        triggerType="click"
        placement="bottom-start"
        className={styles.navLinkItemContainer}
        onOpenChange={setIsOpen}
      >
        {renderLinks()}
      </Menu>
    )
  }

  return (
    <>
      <Trigger isMenuOpen={isOpen} />
      <div
        role="menu"
        className={clsx(styles.navLinkItemContainer, isOpen ? 'display-block' : 'display-none')}
      >
        {renderLinks()}
      </div>
    </>
  )
}

interface NavLinksProps extends ComponentPropsWithoutRef<'nav'> {
  navLinks: NavLinkItem[]
  category?: NavLinksCategory
}

export function NavLinks(props: NavLinksProps): ReactElement {
  const { navLinks, className, category = 'primary', ...rest } = props
  return (
    <nav className={clsx(className)} {...rest}>
      <ul className={clsx(styles.navList, styles[category])}>
        {navLinks.map(link => (
          <li
            key={generateRandomKey()}
            className={clsx(category === 'secondary' && styles.navListItem)}
          >
            <NavLinkItem link={link} category={category} />
          </li>
        ))}
      </ul>
    </nav>
  )
}
